import { toSnakeCase } from 'helper/toSnakeCase'
import { toCamelCase } from './toCamelCase'

/**
 * Utility class for converting object keys between different case formats.
 * Provides static methods to transform object keys recursively, including nested objects and arrays.
 */
export class Converter {
  /**
   * Converts all keys in an object from camelCase to snake_case recursively.
   * Works with nested objects and arrays.
   *
   * @template T - The type of the input object
   * @param {T} camelCaseObject - The object with camelCase keys to convert
   * @returns The transformed object with snake_case keys
   *
   * @example
   * Converter.toSnakeCase({ firstName: 'John', lastName: 'Doe' })
   * Returns: { first_name: 'John', last_name: 'Doe' }
   *
   * @example
   * Converter.toSnakeCase({ userInfo: { firstName: 'John' } })
   * Returns: { user_info: { first_name: 'John' } }
   */
  public static toSnakeCase<T = Object>(camelCaseObject: T) {
    return toSnakeCase(camelCaseObject)
  }

  /**
   * Converts all keys in an object from snake_case to camelCase recursively.
   * Works with nested objects and arrays.
   *
   * @template T - The type of the input object
   * @param {T} snakeCaseObject - The object with snake_case keys to convert
   * @returns The transformed object with camelCase keys
   *
   * @example
   * Converter.toCamelCase({ first_name: 'John', last_name: 'Doe' })
   * Returns: { firstName: 'John', lastName: 'Doe' }
   *
   * @example
   * Converter.toCamelCase({ user_info: { first_name: 'John' } })
   * Returns: { userInfo: { firstName: 'John' } }
   */
  public static toCamelCase<T = Object>(snakeCaseObject: T) {
    return toCamelCase(snakeCaseObject)
  }
}
